<script setup>
// Bulle d'aide IA flottante (feature 'assistant').
// Visible uniquement si le compte a la feature activée. Aide « comment faire »
// uniquement : l'assistant n'accède à aucune donnée du compte.
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { tenantHasFeature } from '@/lib/features';
import { useAssistant } from '@/composables/useAssistant';

const { t, locale } = useI18n();
const route = useRoute();
const auth = useAuthStore();
const { messages, loading, error, ask, reset } = useAssistant();

// Affichée seulement pour un utilisateur tenant authentifié dont le compte a
// la feature. Un SUPERADMIN (pas de tenant) ne la voit pas.
const visible = computed(
  () => auth.isAuthenticated && !auth.isSuperadmin && tenantHasFeature(auth.tenant, 'assistant'),
);

const open = ref(false);
const draft = ref('');
const scrollEl = ref(null);

const errorMessage = computed(() => {
  if (!error.value) return null;
  if (error.value === 'ERR_RATE_LIMIT') return t('assistant.errorRate');
  if (error.value === 'ERR_ASSISTANT_UNAVAILABLE') return t('assistant.errorUnavailable');
  return t('assistant.error');
});

async function scrollToBottom() {
  await nextTick();
  if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight;
}

watch([messages, loading], scrollToBottom, { deep: true });

async function submit() {
  const q = draft.value.trim();
  if (!q || loading.value) return;
  draft.value = '';
  await ask(q, { route: route.name ? String(route.name) : route.path, locale: locale.value });
}

function toggle() {
  open.value = !open.value;
}
</script>

<template>
  <div
    v-if="visible"
    class="fixed bottom-4 right-4 z-50 flex flex-col items-end"
  >
    <!-- Panneau de conversation -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <div
        v-if="open"
        class="mb-3 flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
        style="height: 30rem; max-height: calc(100vh - 6rem)"
      >
        <!-- En-tête -->
        <header class="flex items-center justify-between bg-primary-600 px-4 py-3 text-white">
          <div class="min-w-0">
            <p class="text-sm font-semibold leading-tight">{{ t('assistant.title') }}</p>
            <p class="truncate text-xs text-white/80">{{ t('assistant.subtitle') }}</p>
          </div>
          <div class="flex items-center gap-1">
            <button
              v-if="messages.length"
              type="button"
              class="rounded p-1 text-white/80 hover:bg-white/10 hover:text-white"
              :title="t('assistant.reset')"
              @click="reset"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h5M20 20v-5h-5M5 9a7 7 0 0111.9-3M19 15a7 7 0 01-11.9 3" />
              </svg>
            </button>
            <button
              type="button"
              class="rounded p-1 text-white/80 hover:bg-white/10 hover:text-white"
              :title="t('assistant.close')"
              @click="open = false"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </header>

        <!-- Fil de discussion -->
        <div
          ref="scrollEl"
          class="flex-1 space-y-3 overflow-y-auto bg-gray-50 px-3 py-4"
        >
          <!-- Intro -->
          <div
            v-if="!messages.length"
            class="rounded-xl bg-white px-3 py-3 text-sm text-gray-600 shadow-sm"
          >
            {{ t('assistant.intro') }}
          </div>

          <div
            v-for="(m, i) in messages"
            :key="i"
            class="flex"
            :class="m.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <div
              class="max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm shadow-sm"
              :class="m.role === 'user'
                ? 'bg-primary-600 text-white rounded-br-sm'
                : 'bg-white text-gray-800 rounded-bl-sm'"
            >{{ m.text }}</div>
          </div>

          <!-- Indicateur de réflexion -->
          <div v-if="loading" class="flex justify-start">
            <div class="rounded-2xl rounded-bl-sm bg-white px-3 py-2 text-sm text-gray-400 shadow-sm">
              {{ t('assistant.thinking') }}…
            </div>
          </div>

          <!-- Erreur -->
          <div
            v-if="errorMessage"
            class="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {{ errorMessage }}
          </div>
        </div>

        <!-- Saisie -->
        <form
          class="flex items-end gap-2 border-t border-gray-200 bg-white p-2"
          @submit.prevent="submit"
        >
          <textarea
            v-model="draft"
            rows="1"
            :placeholder="t('assistant.placeholder')"
            class="max-h-24 flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600"
            @keydown.enter.exact.prevent="submit"
          />
          <button
            type="submit"
            :disabled="loading || !draft.trim()"
            class="rounded-lg bg-primary-600 px-3 py-2 text-white disabled:cursor-not-allowed disabled:opacity-40"
            :title="t('assistant.send')"
          >
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
        </form>

        <p class="bg-white px-3 pb-2 text-center text-[11px] leading-tight text-gray-400">
          {{ t('assistant.disclaimer') }}
        </p>
      </div>
    </Transition>

    <!-- Bouton flottant -->
    <button
      type="button"
      class="flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg transition hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
      :title="t('assistant.open')"
      @click="toggle"
    >
      <svg v-if="!open" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M8 10h8M8 14h5M21 12a8 8 0 01-11.5 7.2L3 21l1.8-6.5A8 8 0 1121 12z" />
      </svg>
      <svg v-else class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
</template>
